"use client";

import { Button as HeroButton } from "@heroui/react";
import { cn } from "../../lib/utils";
import buttonStyles from "./Button.module.css";

type HeroButtonColor = React.ComponentProps<typeof HeroButton>["color"];
type ExtendedColor = HeroButtonColor | "soft" | "punch" | "pop" | "white";
type HeroButtonVariant = React.ComponentProps<typeof HeroButton>["variant"];
export type ExtendedVariant = HeroButtonVariant;

type BaseButtonProps = Omit<React.ComponentProps<typeof HeroButton>, "color" | "variant"> & {
  className?: string;
  color?: ExtendedColor;
  variant?: ExtendedVariant;
};

// When isIconOnly is true, aria-label is required for accessibility
type IconOnlyProps = BaseButtonProps & {
  isIconOnly: true;
  "aria-label": string;
};

// When isIconOnly is dynamically toggled (boolean), aria-label is required
// because the button may be icon-only at runtime. Note: aria-label will also
// be applied when isIconOnly resolves to false, so ensure the value is
// meaningful as both an icon-only label and alongside visible text.
type DynamicIconOnlyProps = BaseButtonProps & {
  isIconOnly: boolean;
  "aria-label": string;
};

type StandardProps = BaseButtonProps & {
  isIconOnly?: false;
};

export type ButtonProps = IconOnlyProps | DynamicIconOnlyProps | StandardProps;

export const Button = ({ className, color = "default", variant, radius = "sm", ...props }: ButtonProps) => {
  const { isIconOnly } = props;

  const heroButtonColor =
    color === "soft"
      ? "primary"
      : color === "punch"
        ? "warning"
        : color === "pop" || color === "white"
          ? "secondary"
          : (color as HeroButtonColor);

  return (
    <HeroButton
      color={heroButtonColor}
      variant={variant}
      data-is-icon-only={isIconOnly}
      data-color={color}
      data-radius={radius}
      data-variant={variant}
      className={cn(buttonStyles.button, className)}
      {...props}
    />
  );
};
